import React, { useEffect, useState } from 'react';

import { Modal as ModalAntD } from 'antd';
import QRCodeLib from 'qrcode';
import { QRCodeSVG } from 'qrcode.react';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { countdownFrom } from './tokenCountdown';

import { GeneratedInvite } from '../../../../types/responses/Interoperability';

const Warning = styled.div`
  background: #fff4e0;
  border: 1px solid #eddfb8;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

const WarningTitle = styled.strong`
  display: block;
  color: #8a5300;
  margin-bottom: 4px;
`;

const WarningText = styled.span`
  color: #6b5a2a;
  font-size: 11.5px;
`;

const Split = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
`;

const Left = styled.div`
  flex: 1;
  min-width: 0;
`;

const TokenBox = styled.div`
  background: #f6f8fa;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  word-break: break-all;
  color: #12263f;
`;

const CopyButton = styled.button<{ copied: boolean }>`
  margin-top: 8px;
  background: ${({ copied, theme }) => (copied ? '#e7f6ed' : theme.primary)};
  color: ${({ copied }) => (copied ? '#1e7a45' : '#fff')};
  border: 1px solid ${({ copied, theme }) => (copied ? '#bfe6cf' : theme.primary)};
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
`;

const Expiry = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const Clock = styled.span<{ expired: boolean }>`
  font-family: ui-monospace, monospace;
  font-size: 18px;
  color: ${({ expired }) => (expired ? '#b02525' : '#12263f')};
`;

const Meta = styled.dl`
  margin: 14px 0 0;
  font-size: 11.5px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 3px 0;
  border-top: 1px solid #f0f2f4;
`;

const MetaKey = styled.dt`
  color: #8a9099;
  min-width: 130px;
  margin: 0;
`;

const MetaValue = styled.dd`
  margin: 0;
  color: #3d4653;
`;

const QrSide = styled.div`
  flex: none;
  text-align: center;
`;

const DownloadButton = styled.button`
  margin-top: 8px;
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 11.5px;
`;

const Foot = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #e9ebee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CloseHint = styled.span<{ copied: boolean }>`
  color: ${({ copied }) => (copied ? '#1e7a45' : '#b02525')};
  font-size: 11.5px;
  flex: 1;
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
`;

// O @types do antd 4.10 nao declara children no Modal; mesmo alias que o repo usa.
const Modal: any = ModalAntD;

export type GeneratedTokenModalProps = {
  invite: GeneratedInvite;
  accountCount: number;
  stationCount: number;
  inheritsNewSubaccounts: boolean;
  ttlLabel: string;
  onClose: () => void;
};

/**
 * Tela do token gerado (MV2-1715).
 *
 * É a tela mais sensível do módulo: o token só existe em claro aqui, uma vez.
 * Se a pessoa fechar sem copiar, o único caminho é revogar e gerar outro.
 *
 * O token vive apenas no estado deste componente, vindo da resposta da
 * geração. Não vai para a URL, nem para localStorage, nem para console, nem
 * para ferramenta de telemetria — cada um desses seria uma cópia que sobrevive
 * ao fechamento da tela.
 */
export const GeneratedTokenModal: React.FC<GeneratedTokenModalProps> = ({
  invite,
  accountCount,
  stationCount,
  inheritsNewSubaccounts,
  ttlLabel,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(() => countdownFrom(invite.expires_at));

  /** O contador sai do expires_at do servidor, recalculado a cada segundo. */
  useEffect(() => {
    const timer = setInterval(
      () => setCountdown(countdownFrom(invite.expires_at)),
      1000,
    );
    return () => clearInterval(timer);
  }, [invite.expires_at]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite.token);
      setCopied(true);
    } catch {
      // Sem permissão de área de transferência: o token segue selecionável
      // na tela, então a pessoa ainda consegue copiar à mão.
      setCopied(false);
    }
  };

  /**
   * A arte do QR é gerada no cliente, do token direto para um data URL.
   * Mandar o token para um gerador de QR externo seria entregar o segredo a
   * um terceiro.
   */
  const downloadQrArt = async () => {
    const dataUrl = await QRCodeLib.toDataURL(invite.token, {
      width: 640,
      margin: 2,
    });

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `convite-interoperabilidade-${invite.invite_id}.png`;
    link.click();
  };

  return (
    /*
      Modal do antd. Este era o de 640px sem max-width, que estourava em viewport de
      1280 com a sidebar aberta; o antd cuida da largura e da margem. O footer fica
      proprio porque o texto muda conforme a pessoa ja copiou ou nao o token.
    */
    <Modal
      visible
      width={640}
      title={T('Invite generated')}
      onCancel={onClose}
      footer={
        <Foot>
          <CloseHint copied={copied}>
            {copied
              ? T('Token copied.')
              : T('Did you copy the token? It cannot be recovered.')}
          </CloseHint>
          <CloseButton type="button" onClick={onClose}>
            {copied ? T('Close') : T('I copied it, close')}
          </CloseButton>
        </Foot>
      }
    >
      <div>
          <Warning>
            <WarningTitle>{T('This token will be shown only once')}</WarningTitle>
            <WarningText>
              {T(
                'Copy it now and send it through a secure channel. It is single-use and we store only its hash — if lost, revoke it and generate another.',
              )}
            </WarningText>
          </Warning>

          <Split>
            <Left>
              <TokenBox>{invite.token}</TokenBox>
              <CopyButton type="button" copied={copied} onClick={copy}>
                {copied ? `${T('Copied')} ✓` : T('Copy token')}
              </CopyButton>

              <Expiry>
                <span style={{ color: '#8a9099', fontSize: 11.5 }}>
                  {countdown.expired ? T('Expired') : T('Expires in')}
                </span>
                <Clock expired={countdown.expired}>{countdown.text}</Clock>
                <span style={{ color: '#8a9099', fontSize: 11 }}>TTL {ttlLabel}</span>
              </Expiry>

              {countdown.expired && (
                <WarningText>
                  {T('This token expired. Generate another invite to connect this partner.')}
                </WarningText>
              )}

              <Meta>
                <MetaRow>
                  <MetaKey>{T('Accounts in the contract')}</MetaKey>
                  <MetaValue>
                    {accountCount} {T('accounts')} · {stationCount} {T('stations')}
                    {inheritsNewSubaccounts && ` · ${T('inherits new ones')}`}
                  </MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaKey>{T('Contract')}</MetaKey>
                  <MetaValue>
                    {invite.contract_type === 'BIDIRECTIONAL'
                      ? T('Bidirectional — both apps display')
                      : T('One-way — only their app displays')}
                  </MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaKey>{T('Use')}</MetaKey>
                  <MetaValue>{T('Single use · stored only as a hash')}</MetaValue>
                </MetaRow>
              </Meta>
            </Left>

            <QrSide>
              <QRCodeSVG value={invite.token} size={148} level="M" />
              <div>
                <DownloadButton type="button" onClick={downloadQrArt}>
                  {T('Download QR art')}
                </DownloadButton>
              </div>
            </QrSide>
          </Split>
      </div>
    </Modal>
  );
};
